/**
 * Normaliza uma string, removendo caracteres especiais, acentuação, convertando para minuscula.
 * @param {string} str texto a ser normalizado
 * @returns {boolean}
 */
const normalize = (str) => {
  return str
    // Normaliza para a forma NFD (decomposição de caracteres acentuados)
    .normalize('NFD')
    // Remove todos os caracteres diacríticos (acentos)
    .replace(/[\u0300-\u036f]/g, '')
    // Remove caracteres especiais (mantém apenas letras, números e espaços)
    .replace(/[^\w\s]/gi, '')
    // Substitui múltiplos espaços por um único espaço
    .replace(/\s+/g, ' ')
    // Remove espaços no início e no final
    .trim()
    // Converte para minúsculas
    .toLowerCase();
}

/** @type {import('../businessplan/businessplan.types').FrontendWorkflowStep[]} lista de todas as etapas criadas*/
export const allSteps = [];

/**
 * Cria uma etapa do workflow com mínimo de informações
 * @param {string} name nome da etapa, também usado para identificar paridade com workflow do back
 * @returns {import('../businessplan/businessplan.types').FrontendWorkflowStep}
 */
export const createWorkflow = (
  name = "",
) => {
  const step = {
    id: allSteps.length + 1,
    name,
    requiredHierarchicalLevels: [],
    defineReviewers: true,
    selfReviewer: false,
    shareReviewersWith: [],
    maxReviewers: 1,
    minReviewers: 1,
    adjustmentStep: this,
    nextStep: this,
    reprovalStep: this,
    isEquivalent(workflowName = '') {
      return normalize(workflowName) === normalize(this.name);
    }
  };

  allSteps.push(step);

  return step;
};

export const waitingInitialStep = createWorkflow("Enviado para Análise Inicial FP&A");
export const initialFPeAStep = createWorkflow("Em Análise Inicial FP&A");
export const adjustmentStep = createWorkflow("Ajustes");
export const regularFPeAStep = createWorkflow("Em Análise FP&A");
export const regularCFOStep = createWorkflow("Em Análise CFO");
export const executiveStep = createWorkflow("Aprovado CFO - Em análise Executiva");
export const waitingAccountingStep = createWorkflow("Aguardando Análise Contábil");
export const accountingStep = createWorkflow("Em Análise Contábil");
export const adjustmentAccountingStep = createWorkflow("Ajuste Contábil");
export const approvalStep = createWorkflow("Aprovado");
export const reprovalStep = createWorkflow("Reprovado");

// Define próxima etapa
waitingInitialStep.nextStep = initialFPeAStep;
initialFPeAStep.nextStep = regularCFOStep;
adjustmentStep.nextStep = regularFPeAStep;
regularFPeAStep.nextStep = regularCFOStep;
regularCFOStep.nextStep = executiveStep;
executiveStep.nextStep = waitingAccountingStep;
waitingAccountingStep.nextStep = accountingStep;
accountingStep.nextStep = approvalStep;
adjustmentAccountingStep.nextStep = accountingStep;

// Define etapa de ajustes
waitingInitialStep.adjustmentStep = adjustmentStep;
initialFPeAStep.adjustmentStep = adjustmentStep;
regularFPeAStep.adjustmentStep = adjustmentStep;
regularCFOStep.adjustmentStep = adjustmentStep;
executiveStep.adjustmentStep = adjustmentStep;
waitingAccountingStep.adjustmentStep = adjustmentStep;
accountingStep.adjustmentStep = adjustmentAccountingStep;

// Define etapa de reprovação
waitingInitialStep.reprovalStep = reprovalStep;
initialFPeAStep.reprovalStep = reprovalStep;
regularFPeAStep.reprovalStep = reprovalStep;
regularCFOStep.reprovalStep = reprovalStep;
executiveStep.reprovalStep = reprovalStep;
waitingAccountingStep.reprovalStep = reprovalStep;
accountingStep.reprovalStep = reprovalStep;
approvalStep.reprovalStep = reprovalStep;

// Defini exceções da regra de 1 aprovação por etapa
waitingInitialStep.maxReviewers = 0;
waitingInitialStep.minReviewers = 0;
waitingInitialStep.defineReviewers = false;
adjustmentStep.maxReviewers = 0;
adjustmentStep.minReviewers = 0;
adjustmentStep.defineReviewers = false;
executiveStep.maxReviewers = 3;
executiveStep.minReviewers = 1;
waitingAccountingStep.maxReviewers = 0;
waitingAccountingStep.minReviewers = 0;
waitingAccountingStep.defineReviewers = false;
adjustmentAccountingStep.maxReviewers = 0;
adjustmentAccountingStep.minReviewers = 0;
adjustmentAccountingStep.defineReviewers = false;
approvalStep.maxReviewers = 0;
approvalStep.minReviewers = 0;
approvalStep.defineReviewers = false;
reprovalStep.maxReviewers = 0;
reprovalStep.minReviewers = 0;
reprovalStep.defineReviewers = false;

// Defini quais etapas o revisor se indica em vez de ser indicado
waitingInitialStep.selfReviewer = true;
waitingAccountingStep.selfReviewer = true;

// Defini quais outras etapas devem ter o mesmo revisor de outra etapa
waitingInitialStep.shareReviewersWith = [initialFPeAStep, regularFPeAStep];
waitingAccountingStep.shareReviewersWith = [accountingStep];

// Impede que o pareamento ocorra mais de uma vez
let pairedWithBackend = false;

/**
 * Atribui informações importantes do backend as etapas alocadas localmente
 * @param {import('../../businessplan.types').Workflow[]} parameters lista de workflows do backend
 */
export const setParityWithBackend = (parameters) => {
  if (!Array.isArray(parameters) || pairedWithBackend) {
    return;
  }

  allSteps.forEach((step) => {
    const parameter = parameters.find(({ nome }) => step.isEquivalent(nome));

    if (parameter) {
      step.id = parameter.id;
      step.requiredHierarchicalLevels = parameter.revisores_tipo_id ?? [];
      // Outros dados podem estar errado no back
    }
  });

  pairedWithBackend = true;
};

/**
 * Retorna se o workflow local já foi pareado com as configurações do backend
 * @returns {boolean}
 */
export const isPairedWithBackend = () => pairedWithBackend;

/**
 * Retorna a etapa do workflow local compativel com a etapa atual do backend
 * @param {string} workflowName nome do workflow atual segundo o backend
 * @returns {import('../businessplan/businessplan.types').FrontendWorkflowStep | undefined}
 */
export const getCurrentStep = (workflowName) => {
  return allSteps.find((step) => step.isEquivalent(workflowName));
};
